import type { PermissionsResponse } from "@shared/permissions";

/**
 * Persistent fallback for the partner-portal permissions gating query.
 *
 * When the upstream Eatzy permissions service is briefly unreachable
 * (502/504/network), we serve the user's last-known-good permissions out of
 * localStorage so the user stays logged in and can keep working through the
 * outage. Bounded by a short freshness window so a recently-revoked role
 * cannot keep portal access for long.
 *
 * The cache stores ONE entry (the most recently authenticated user's
 * permissions) keyed by user id inside the payload. On user switch / logout
 * we wipe the entry entirely so a previous user's roles can never bleed
 * into the next session.
 */

export const PERMISSIONS_CACHE_STORAGE_KEY = "kudoz:permissions-cache:v1";
export const PERMISSIONS_CACHE_FRESHNESS_MS = 15 * 60 * 1000;

export interface CachedPermissions {
  userId: string;
  data: PermissionsResponse;
  storedAt: number;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getStorage(): StorageLike | null {
  try {
    const g = globalThis as { localStorage?: StorageLike };
    if (g && g.localStorage) return g.localStorage;
  } catch {
    // SSR / privacy mode / restricted iframe — no persistence available.
  }
  return null;
}

function normalizeUserId(
  id: string | number | null | undefined
): string | null {
  if (id === null || id === undefined) return null;
  const s = String(id);
  return s.length > 0 ? s : null;
}

export function readPermissionsCache(): CachedPermissions | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(PERMISSIONS_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedPermissions>;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.userId !== "string" || parsed.userId.length === 0)
      return null;
    if (
      typeof parsed.storedAt !== "number" ||
      !Number.isFinite(parsed.storedAt)
    )
      return null;
    if (!parsed.data || typeof parsed.data !== "object") return null;
    return parsed as CachedPermissions;
  } catch {
    return null;
  }
}

export function writePermissionsCache(
  userId: string | number | null | undefined,
  data: PermissionsResponse,
  now: number = Date.now()
): void {
  const id = normalizeUserId(userId);
  if (!id) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    const payload: CachedPermissions = { userId: id, data, storedAt: now };
    storage.setItem(PERMISSIONS_CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / serialization errors are non-fatal — the cache is best-effort.
  }
}

export function clearPermissionsCache(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(PERMISSIONS_CACHE_STORAGE_KEY);
  } catch {
    // noop
  }
}

export function isPermissionsCacheFresh(
  cached: CachedPermissions | null,
  expectedUserId: string | number | null | undefined,
  now: number = Date.now()
): boolean {
  if (!cached) return false;
  const id = normalizeUserId(expectedUserId);
  if (!id) return false;
  if (cached.userId !== id) return false;
  const age = now - cached.storedAt;
  if (!Number.isFinite(age)) return false;
  // Reject future timestamps (clock-skew / corruption) AND anything past the window.
  return age >= 0 && age < PERMISSIONS_CACHE_FRESHNESS_MS;
}

/**
 * Convenience: read the cache and return it only if it's fresh AND belongs
 * to the expected user. Returns null otherwise. Callers use this to decide
 * whether to render a protected page using cached roles during an upstream
 * outage.
 */
export function getFreshPermissionsCache(
  expectedUserId: string | number | null | undefined,
  now: number = Date.now()
): CachedPermissions | null {
  const cached = readPermissionsCache();
  if (isPermissionsCacheFresh(cached, expectedUserId, now)) return cached;
  return null;
}
